/*
 * COUNTRY FIELD — the checkout's shipping country: a searchable list of every
 * country the shop delivers to, on all its sale channels.
 *
 *   <country-field :rows="cartConfig.channelCountries" :current="countryShipping.id" :currency="cartConfig.currencyCode"
 *                  :note="channelNote" @select="pickCountry" @switch="switchChannel"></country-field>
 *
 *   rows     [{id, code, name, local, currency, url}] (ChannelSwitcher::countryRows, server side)
 *   select   a country of this channel's currency: the checkout only changes its country
 *   switch   a country of another currency: after a confirmation, the parent moves the customer to that channel
 *
 * A phone gets a bottom sheet, a desktop a dropdown card under the field. The
 * theme styles it (.country-field*), the texts are window.COUNTRY_TEXTS.
 */
// a missing translation group comes back as a plain string: no texts, not String.prototype methods
const TEXTS = () => (typeof window.COUNTRY_TEXTS === 'object' && window.COUNTRY_TEXTS) || {};
const mobile = () => window.matchMedia && window.matchMedia('(max-width: 47.999em)').matches;

export const CountryField = {
    name: 'CountryField',
    props: {
        rows: {type: Array, default: () => []},
        current: {default: null},
        currency: {type: String, default: ''},
        note: {type: String, default: ''},
    },
    emits: ['select', 'switch'],
    data() {
        return {open: false, sheet: false, q: '', pending: null};
    },
    computed: {
        t() { return TEXTS(); },
        selected() {
            return this.rows.find((r) => String(r.id) === String(this.current))
                || this.rows.find((r) => String(r.currency ?? '').toUpperCase() === this.currency.toUpperCase()) || null;
        },
        list() {
            // accent-insensitive, and in the page's language too ("Románia" finds "Romania" / "România")
            const fold = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            const needle = fold(this.q.trim());
            const lang = (document.documentElement.lang || 'en').slice(0, 2);
            return this.rows
                .filter((r) => !needle || [r.name, r.local, r.code, this.regionName(r.code)].some((s) => s && fold(s).includes(needle)))
                .slice().sort((a, b) => String(a.name).localeCompare(String(b.name), lang));
        },
        label() { return this.selected ? (this.selected.label || this.selected.name) : ''; },
        currencyWord() { return (this.t.currency_to ?? {})[this.pending?.currency] || this.pending?.currency || ''; },
        ask() {
            return String(this.t.ask ?? '').replace(':country', this.pending?.name ?? '').replace(':currency', this.currencyWord);
        },
        go() { return String(this.t.go ?? '').replace(':currency', this.currencyWord); },
    },
    mounted() {
        this._key = (e) => { if (e.key === 'Escape' && this.open) this.close(); };
        this._away = (e) => { if (this.open && !this.sheet && !e.composedPath().includes(this.$el)) this.close(); };
        document.addEventListener('keydown', this._key);
        document.addEventListener('click', this._away);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this._key);
        document.removeEventListener('click', this._away);
        document.documentElement.classList.remove('country-field-open');
    },
    methods: {
        toggle() { this.open ? this.close() : this.show(); },
        show() {
            this.sheet = mobile(); this.q = ''; this.pending = null; this.open = true;
            if (this.sheet) document.documentElement.classList.add('country-field-open');
            this.$nextTick(() => {
                // the current country in the middle of the list (the list scrolls, not the page)
                const list = this.$refs.list;
                const on = list && list.querySelector('.on');
                if (on) list.scrollTop = Math.max(0, on.offsetTop - list.offsetTop - (list.clientHeight - on.offsetHeight) / 2);
                if (!this.sheet && this.$refs.q) this.$refs.q.focus({preventScroll: true});
            });
        },
        close() {
            // the focus was inside the panel (which goes away): it returns to the field's button
            const inside = this.$refs.panel && this.$refs.panel.contains(document.activeElement);
            this.open = false; this.pending = null;
            document.documentElement.classList.remove('country-field-open');
            if (inside || document.activeElement === document.body) this.$nextTick(() => this.$refs.btn && this.$refs.btn.focus({preventScroll: true}));
        },
        // in the bottom sheet (phones) the Tab stays inside it
        trap(e) {
            if (e.key !== 'Tab' || !this.sheet || !this.$refs.panel) return;
            const list = [...this.$refs.panel.querySelectorAll('button, input')].filter((x) => x.offsetParent !== null);
            if (!list.length) return;
            const first = list[0], last = list[list.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        },
        choose(row) {
            if (this.selected && String(row.id) === String(this.selected.id)) { this.close(); return; }
            if (String(row.currency ?? '').toUpperCase() === this.currency.toUpperCase()) { this.$emit('select', row); this.close(); return; }
            this.pending = row;
            // the confirmation sits at the bottom of the sheet: the keyboard (which floats over the sheet) goes down
            if (this.$refs.q && document.activeElement === this.$refs.q) this.$refs.q.blur();
        },
        confirm() { if (this.pending) this.$emit('switch', this.pending); },
        // the country's name in the page's language (hu: "Románia"), for the search
        regionName(code) {
            if (!this._names) {
                try { this._names = new Intl.DisplayNames([document.documentElement.lang || 'en'], {type: 'region'}); } catch (e) { this._names = false; }
            }
            if (!this._names || !/^[A-Za-z]{2}$/.test(String(code || ''))) return '';
            try { return this._names.of(String(code).toUpperCase()) || ''; } catch (e) { return ''; }
        },
    },
    template: `
<div class="country-field">
    <button type="button" class="country-field-btn" ref="btn" aria-haspopup="dialog" :aria-expanded="open ? 'true' : 'false'" @click="toggle">
        <small>{{ t.label }}</small><span class="v">{{ label }}</span><i class="c">{{ selected ? selected.currency : '' }}</i>
    </button>
    <div class="country-field-note" v-if="note">{{ note }}</div>
    <teleport to="body" :disabled="!sheet">
        <div class="country-field-scrim" v-if="open && sheet" @click="close"></div>
        <div class="country-field-panel" :class="sheet ? 'is-sheet' : 'is-pop'" role="dialog" :aria-modal="sheet ? 'true' : null" :aria-label="t.title" v-if="open" ref="panel" @keydown="trap">
            <template v-if="sheet">
                <div class="country-field-handle"></div>
                <div class="country-field-hd"><h3>{{ t.title }}</h3><button type="button" class="x" :aria-label="t.close" @click="close">&times;</button></div>
            </template>
            <label class="country-field-q"><span class="sr-only">{{ t.search }}</span><input ref="q" type="search" v-model="q" autocomplete="off" spellcheck="false" :placeholder="t.search"></label>
            <div class="country-field-list" role="group" :aria-label="t.title" ref="list">
                <button type="button" v-for="r in list" :key="r.id" class="country-field-row" :class="{on: selected && String(r.id) === String(selected.id), pick: pending && pending.id === r.id}"
                        :aria-current="selected && String(r.id) === String(selected.id) ? 'true' : null" @click="choose(r)">
                    <span class="n">{{ r.name }}<small v-if="r.local && r.local !== r.name">{{ r.local }}</small></span>
                    <span class="tick" aria-hidden="true" v-if="(selected && String(r.id) === String(selected.id)) || (pending && pending.id === r.id)"></span>
                    <span class="cur">{{ r.currency }}</span>
                </button>
                <div class="country-field-none" v-if="!list.length">{{ t.none }}</div>
            </div>
            <div class="country-field-foot">
                <div class="country-field-conf" v-if="pending">
                    <p>{{ ask }}</p>
                    <button type="button" class="go" @click="confirm">{{ go }}</button><button type="button" class="no" @click="pending = null">{{ t.cancel }}</button>
                </div>
            </div>
        </div>
    </teleport>
</div>`,
};

window.CountryField = CountryField;
export default CountryField;
